import React from 'react';

import cn from '~/Utils/cn';

/**
 * Internal to `components/ui/`: the built-in `label` of a Base UI control (Switch, Checkbox). Not a
 * public component; consumers pass `label` to the control instead.
 *
 * A real `<label>` wraps the control: Base UI finds it through the hidden input's `.labels` and
 * points the control's `aria-labelledby` at it, so the text names the control for assistive
 * technology, and clicking the text toggles it. Without a label the control renders bare.
 */
const OptionalLabel = ({
  label,
  id,
  disabled,
  className,
  children: control,
}: {
  label?: React.ReactNode;
  /** The control's `id`, which lands on its hidden input. */
  id?: string;
  disabled?: boolean;
  /** Merged over the defaults, e.g. to change the gap a control's own padding already provides. */
  className?: string;
  children: React.ReactNode;
}) => {
  if (label) {
    return (
      <label
        className={cn(
          'inline-flex cursor-pointer items-center gap-4 align-middle',
          disabled && 'cursor-default',
          className
        )}
        htmlFor={id}
      >
        {control}
        <span className={cn({ 'opacity-40': disabled })}>{label}</span>
      </label>
    );
  }

  return control;
};

export default OptionalLabel;
